<template>
    <span v-if="!editManualFeeForm"
          @click="showEditFeeForm"
          :class="[editable ? 'click-to-edit' : 'text-muted']">
        <template v-if="booking.total_cost !== null">
            {{ formatCurrency(manualFeeToShow) }}
        </template>
        <template v-else> Not specified </template>
    </span>
    <div v-if="confirmingChanges">
        <p>
            Are you sure you wish to adjust this booking fee to
            {{ formatCurrency(manualFeeAmount.value) }}?
        </p>
        <div class="d-flex justify-content-end">
            <Button class="me-1"
                    color="light"
                    size="small"
                    @click="cancelChanges"
            >Cancel
            </Button>
            <Button :loading="storing"
                    color="danger"
                    size="small"
                    @click="storeChanges">Confirm
            </Button>
        </div>
    </div>
    <div v-else-if="editManualFeeForm" class="container">
        <div class="row">
            <div class="col">
                <FormDollarInput
                    v-model:input="manualFeeAmount"
                    label="Enter Booking Fee"
                    placeholder="e.g. 5.50"
                    step=".01"
                    input-group-class="input-group input-group-sm"
                    input-class="form-control form-control-sm"
                />
                <span class="form-text">The value is GST exclusive</span>
            </div>
            <div class="col-auto mt-4">
                <FormSwitch
                    label="Plus GST"
                    v-model="manualFeeHasGst"
                />
            </div>
        </div>
        <div class="row">
            <div class="col d-flex justify-content-end mt-2">
                <Button class="me-1"
                        color="light"
                        size="small"
                        @click="cancelChanges">Cancel
                </Button>
                <Button :loading="storing"
                        color="primary"
                        size="small"
                        @click="confirmChanges">Save
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import Booking from "@classes/Booking";
import { BookingEventType } from "@classes/BookingEvent";
import { formatCurrency } from "@classes/Helpers/Currency";
import Button from "@components/Button.vue"
import { FormDollarInput } from "@components/Forms";
import FormSwitch from "@components/Forms/FormSwitch.vue";
import { ToastInterface } from "@components/toast";
import BookingResource from "@customTypes/resources/BookingResource";
import { computed, inject, ref } from "vue";
import { Ref } from "vue/dist/vue";

const toast: ToastInterface = <ToastInterface>inject("toast");

const props = withDefaults(
    defineProps<{
        booking: BookingResource;
        readOnly?: boolean;
    }>(),
    {
        readOnly: false,
    }
);

const booking: Ref<BookingResource> = computed((): BookingResource => props.booking);
const manualFeeHasGst: Ref<boolean> = ref<boolean>(booking.value?.gst > 0);
const manualFeeToEdit = computed(() => ((manualFeeHasGst.value ? booking.value?.subtotal : booking.value?.total_cost) ?? 0) / 100);
const manualFeeToShow = computed(() => ((booking.value?.total_cost) ?? 0) / 100);

const manualFeeAmount = ref<{
    value: string;
    required: boolean;
    valid: boolean;
    errors: string[];
}>({
    value: manualFeeToEdit.value?.toString() ?? "0",
    required: false,
    valid: true,
    errors: [],
});

const emit = defineEmits(["updated"]);

const editable = computed(() => {
    return (
        !props.readOnly &&
        (!props.booking.actual_pick_up_time ||
            props.booking?.events?.filter(
                (event) => event.type === BookingEventType.PICK_UP
            ).length === 0)
    );
});

const editManualFeeForm = ref(false);
const showEditFeeForm = function () {
    if (!editable.value) {
        return;
    }

    editManualFeeForm.value = true;
};

const cancelChanges = () => {
    editManualFeeForm.value = false;
    confirmingChanges.value = false;
};

const confirmingChanges = ref(false);
const confirmChanges = () => {
    confirmingChanges.value = true;
};

const storing = ref(false);
const storeChanges = function () {
    storing.value = true;
    Booking.update(props.booking, {
        manual_fee_amount: Number(manualFeeAmount.value.value),
        manual_fee_has_gst: manualFeeHasGst.value,
    })
        .then((response) => {
            emit("updated", response.data);
            editManualFeeForm.value = false;
        })
        .catch((error) => {
            if (error.response.status && error.response.status === 422) {
                manualFeeAmount.value.errors = [
                    error.response.data.errors.manual_fee_amount[0],
                ];
            } else {
                toast.error(
                    error?.response?.data?.message ?? "Something went wrong. Our system administrators have been notified."
                );
            }
        })
        .finally(() => {
            storing.value = false;
            confirmingChanges.value = false;
        });
};
</script>
